import { useState, useRef, useCallback, useEffect } from "react";
import type { RecordingSource } from "@/types/recording";

export type RecordingState = "idle" | "requesting" | "recording" | "paused" | "stopped";
export type PermissionStatus = "granted" | "denied" | "prompt" | "unknown";

interface UseAudioRecordingReturn {
  recordingState: RecordingState;
  currentTime: number;
  audioLevel: number;
  audioBlob: Blob | null;
  permissionStatus: PermissionStatus;
  startRecording: (mode?: RecordingSource) => Promise<void>;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  requestPermission: () => Promise<boolean>;
  resetRecording: () => void;
}

export function useAudioRecording(): UseAudioRecordingReturn {
  const [recordingState, setRecordingState] = useState<RecordingState>("idle");
  const [currentTime, setCurrentTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>("unknown");

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0);

  // Check permission status on mount
  useEffect(() => {
    checkPermission();
  }, []);

  const checkPermission = async () => {
    try {
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: "microphone" as PermissionName });
        setPermissionStatus(result.state as PermissionStatus);
        result.onchange = () => {
          setPermissionStatus(result.state as PermissionStatus);
        };
      }
    } catch {
      // Some browsers don't support querying microphone permission
      setPermissionStatus("unknown");
    }
  };

  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      setRecordingState("requesting");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the stream immediately, we just needed permission
      stream.getTracks().forEach(track => track.stop());
      setPermissionStatus("granted");
      setRecordingState("idle");
      return true;
    } catch (error) {
      console.error("Microphone permission denied:", error);
      setPermissionStatus("denied");
      setRecordingState("idle");
      return false;
    }
  }, []);

  const updateAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteTimeDomainData(dataArray);

    // Calculate RMS (root mean square) for volume level
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const value = (dataArray[i] - 128) / 128;
      sum += value * value;
    }
    const rms = Math.sqrt(sum / dataArray.length);
    
    // Normalize to 0-1 range with some amplification
    const normalizedLevel = Math.min(1, rms * 3);
    setAudioLevel(normalizedLevel);

    if (recordingState === "recording") {
      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
    }
  }, [recordingState]);

  const startRecording = useCallback(async (mode: RecordingSource = "microphone") => {
    try {
      setRecordingState("requesting");

      let stream: MediaStream;
      if (mode === "browser-tab") {
        if (!navigator.mediaDevices?.getDisplayMedia) {
          setRecordingState("idle");
          throw new Error("TAB_RECORDING_UNSUPPORTED");
        }
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true,
        });
        const audioTracks = stream.getAudioTracks();
        if (audioTracks.length === 0) {
          stream.getTracks().forEach((t) => t.stop());
          setRecordingState("idle");
          throw new Error("NO_TAB_AUDIO");
        }
        // Drop video tracks — we only need audio.
        stream.getVideoTracks().forEach((t) => t.stop());
      } else {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
      }

      streamRef.current = stream;
      setPermissionStatus("granted");
      
      // Set up audio context for visualization
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);

      // Set up MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/ogg";

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
      };

      mediaRecorder.start(1000); // Collect data every second
      setRecordingState("recording");
      startTimeRef.current = Date.now();
      pausedTimeRef.current = 0;

      // Start timer
      timerRef.current = window.setInterval(() => {
        const elapsed = (Date.now() - startTimeRef.current) / 1000 + pausedTimeRef.current;
        setCurrentTime(Math.floor(elapsed));
      }, 100);

      // Start audio level monitoring
      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
    } catch (error) {
      console.error("Error starting recording:", error);
      const msg = error instanceof Error ? error.message : "";
      if (msg !== "NO_TAB_AUDIO" && msg !== "TAB_RECORDING_UNSUPPORTED") {
        setPermissionStatus("denied");
      }
      setRecordingState("idle");
      throw error;
    }
  }, [updateAudioLevel]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && recordingState !== "idle" && recordingState !== "stopped") {
      mediaRecorderRef.current.stop();
      
      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      // Clean up audio context
      if (sourceRef.current) {
        sourceRef.current.disconnect();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }

      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Stop audio level monitoring
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      setAudioLevel(0);
      setRecordingState("stopped");
    }
  }, [recordingState]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && recordingState === "recording") {
      mediaRecorderRef.current.pause();
      
      // Store elapsed time
      pausedTimeRef.current += (Date.now() - startTimeRef.current) / 1000;
      
      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Stop audio level monitoring
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      setAudioLevel(0);
      setRecordingState("paused");
    }
  }, [recordingState]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && recordingState === "paused") {
      mediaRecorderRef.current.resume();
      startTimeRef.current = Date.now();
      setRecordingState("recording");

      // Restart timer
      timerRef.current = window.setInterval(() => {
        const elapsed = (Date.now() - startTimeRef.current) / 1000 + pausedTimeRef.current;
        setCurrentTime(Math.floor(elapsed));
      }, 100);

      // Restart audio level monitoring
      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
    }
  }, [recordingState, updateAudioLevel]);

  const resetRecording = useCallback(() => {
    // Stop any ongoing recording
    if (mediaRecorderRef.current && recordingState !== "idle") {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        // Already stopped
      }
    }

    // Stop all tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    // Clean up audio context
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch {
        // Already disconnected
      }
    }
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch {
        // Already closed
      }
    }

    // Stop timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Stop audio level monitoring
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Reset all state
    chunksRef.current = [];
    setAudioBlob(null);
    setCurrentTime(0);
    setAudioLevel(0);
    setRecordingState("idle");
    pausedTimeRef.current = 0;
  }, [recordingState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch {
          // Already closed
        }
      }
    };
  }, []);

  return {
    recordingState,
    currentTime,
    audioLevel,
    audioBlob,
    permissionStatus,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    requestPermission,
    resetRecording,
  };
}
